import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@shared/utils'
import { tooltipSurface } from './tooltip'

/**
 * Popup rê chuột CHUNG của cả app (chủ dự án yêu cầu 03/11/2026, luật 3b CLAUDE.md). Gắn MỘT lần ở gốc app (`App.tsx`).
 *
 * 1. Phần tử có `title`: thay tooltip mặc định của trình duyệt (chậm, kiểu hệ điều hành, không theo sáng / tối) bằng popup
 *    cùng kiểu `TooltipContent`. Lúc rê vào thì cất `title` sang `data-tip-title` (trình duyệt khỏi hiện bản của nó), rời ra
 *    thì trả lại. Viết code vẫn dùng `title` như thường. Trong SVG: dùng `data-tip` (KHÔNG dùng thẻ con `<title>` – trình duyệt
 *    hiện bản mặc định, lớp này không chặn được).
 * 2. Chữ bị cắt mà không có `title`: ô nhập (`<input>` trừ mật khẩu, ô `role="combobox"`, ô `data-overflow-tip`) và chữ
 *    `truncate` / `line-clamp-*` → hiện đủ chữ, CHỈ khi thật sự bị cắt.
 *
 * Chạy cả với ô / nút KHÓA (`disabled`) – lý do chính của yêu cầu: ô khóa không bấm vào cuộn chữ được. Ô khóa có
 * `pointer-events: none` (Input, Button) hoặc trình duyệt nuốt sự kiện chuột của nó, nên không chờ sự kiện trên ô:
 * nghe `pointermove` ở `document`, lấy phần tử dưới con trỏ rồi dò phần tử khóa ngay dưới nó theo tọa độ.
 */

const FIELD_SELECTOR = 'input, button[role="combobox"], [data-overflow-tip]'
const TITLE_SELECTOR = '[title], [data-tip-title], [data-tip]'
const CANDIDATE_SELECTOR = `${TITLE_SELECTOR}, ${FIELD_SELECTOR}`
/** `title` của khung nhúng chỉ để đọc màn hình – trình duyệt không hiện; con trỏ vào trong khung thì trang không còn nghe được chuột */
const IGNORE_SELECTOR = 'iframe, embed, object'
const DISABLED_SELECTOR = [FIELD_SELECTOR, TITLE_SELECTOR]
  .join(', ')
  .split(', ')
  .map((s) => `${s}:disabled`)
  .join(', ')
/** Ô không có chữ để hiện (hoặc không được lộ chữ: mật khẩu) */
const SKIP_INPUT_TYPES = new Set(['password', 'checkbox', 'radio', 'range', 'file', 'hidden', 'color', 'button', 'submit', 'reset', 'image'])
const SHOW_DELAY_MS = 300 // bằng `TooltipProvider delayDuration`
/** Vừa có popup mà rê sang chỗ khác cũng có popup → hiện ngay, như Radix `skipDelayDuration` */
const SKIP_DELAY_MS = 300
/** Dò chữ bị cắt: phần tử trúng + tối đa ngần này tầng cha */
const TRUNCATE_ANCESTORS = 3
const GAP = 4

type Tip = { text: string; rect: DOMRect }

let measureCanvas: HTMLCanvasElement | null = null

/** Bề rộng chữ của ô `<input>` (dự phòng khi `scrollWidth` của ô không phản ánh chữ tràn) */
function inputTextWidth(el: HTMLInputElement): number {
  measureCanvas ??= document.createElement('canvas')
  const ctx = measureCanvas.getContext('2d')
  if (!ctx) return 0
  const cs = getComputedStyle(el)
  ctx.font = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`
  return ctx.measureText(el.value).width
}

function isOverflowing(el: Element): boolean {
  return el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1
}

function isTruncateStyled(el: Element): boolean {
  const cs = getComputedStyle(el)
  return cs.textOverflow === 'ellipsis' || (cs.webkitLineClamp !== '' && cs.webkitLineClamp !== 'none')
}

/** Chữ đầy đủ của ô nhập nếu đang bị cắt, không thì null */
function truncatedFieldText(field: Element): string | null {
  if (field instanceof HTMLInputElement) {
    if (SKIP_INPUT_TYPES.has(field.type) || !field.value) return null
    if (field.scrollWidth > field.clientWidth + 1) return field.value
    const cs = getComputedStyle(field)
    const inner = field.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight)
    return inputTextWidth(field) > inner + 1 ? field.value : null
  }
  // ô chọn dựng trên nút: chữ nằm trong một <span> có `truncate` / `line-clamp-1`
  for (const node of [field, ...Array.from(field.querySelectorAll('span'))]) {
    const text = node.textContent?.trim()
    if (text && isOverflowing(node)) return text
  }
  return null
}

/** Chữ bị cắt (`truncate` / `line-clamp`) ở phần tử trúng hoặc vài tầng cha */
function truncatedTextAt(hit: Element): { el: Element; text: string } | null {
  let el: Element | null = hit
  for (let i = 0; el && i <= TRUNCATE_ANCESTORS; i++, el = el.parentElement) {
    if (isTruncateStyled(el)) {
      const text = el.textContent?.trim()
      return text && isOverflowing(el) ? { el, text } : null
    }
  }
  return null
}

function titleOf(el: Element): string {
  return (el.getAttribute('title') ?? el.getAttribute('data-tip-title') ?? el.getAttribute('data-tip') ?? '').trim()
}

function contains(rect: DOMRect, x: number, y: number) {
  return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom
}

/** Dò phần tử khóa nằm ngay dưới khung trúng con trỏ: tối đa SCAN_DEPTH tầng / SCAN_LIMIT phần tử */
const SCAN_DEPTH = 3
const SCAN_LIMIT = 200

/**
 * Phần tử khóa `pointer-events: none` → phần tử trúng là khung chứa nó (thường là cha / ông). Không `querySelectorAll` cả cây:
 * con trỏ nằm trên khoảng trống của khung lớn (lề trang, bảng 5.000 dòng) thì mỗi khung hình phải đo hàng nghìn ô (đo 03/11/2026:
 * 30.000 ô → 4–9 ms / lần, sát ngân sách 16 ms). Chỉ đi vài tầng con gần nhất, có trần số phần tử.
 */
function disabledUnder(hit: Element, x: number, y: number): Element | null {
  let level: Element[] = [hit]
  let seen = 0
  for (let depth = 0; depth < SCAN_DEPTH && level.length; depth++) {
    const next: Element[] = []
    for (const parent of level) {
      for (const el of Array.from(parent.children)) {
        if (++seen > SCAN_LIMIT) return null
        if (el.matches(DISABLED_SELECTOR)) {
          if (contains(el.getBoundingClientRect(), x, y)) return el
        } else next.push(el)
      }
    }
    level = next
  }
  return null
}

type Target = { el: Element; text: () => string | null }

/** Phần tử cần popup dưới con trỏ (kể cả phần tử khóa không nhận chuột) + cách lấy chữ */
export function targetAt(x: number, y: number): Target | null {
  const hit = document.elementFromPoint(x, y)
  if (!hit || hit.closest(IGNORE_SELECTOR)) return null
  const el = hit.closest(CANDIDATE_SELECTOR) ?? disabledUnder(hit, x, y)
  if (el) {
    if (titleOf(el)) {
      return {
        el,
        // đọc lại mỗi lần: React có thể đổi `title` trong lúc đang rê
        text: () => {
          const title = titleOf(el)
          if (!title) return null
          // `title` trùng đúng chữ đang hiện (chữ cắt kèm title = chính nó) → chỉ hiện khi bị cắt
          const sameAsText = title === el.textContent?.trim()
          return !sameAsText || isOverflowing(el) || truncatedTextAt(hit) ? title : null
        },
      }
    }
    if (el.matches(FIELD_SELECTOR)) return { el, text: () => truncatedFieldText(el) }
  }
  const cut = truncatedTextAt(hit)
  return cut ? { el: cut.el, text: () => cut.text } : null
}

export function HoverTip() {
  const [tip, setTip] = useState<Tip | null>(null)
  const boxRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let frame = 0
    let timer = 0
    let current: Element | null = null
    let currentTarget: Target | null = null
    let shown = false
    /** Vừa bấm vào phần tử → không hiện lại cho tới khi rời nó (như tooltip của trình duyệt) */
    let suppressed: Element | null = null
    let lastShownAt = 0
    let point = { x: 0, y: 0 }
    /** React đặt lại `title` trong lúc đang rê (giá trị đổi) → cất lại ngay, kẻo trình duyệt hiện bản mặc định; popup đổi chữ theo */
    const observer = new MutationObserver(() => {
      if (!current?.hasAttribute('title')) return
      current.removeAttribute('data-tip-title')
      stash(current)
      if (shown && currentTarget) show(currentTarget)
    })

    /** Cất `title` để trình duyệt không hiện tooltip của nó chồng lên popup */
    const stash = (el: Element) => {
      const t = el.getAttribute('title')
      if (t === null) return
      el.setAttribute('data-tip-title', t)
      el.removeAttribute('title')
    }
    const restore = (el: Element | null) => {
      if (!el?.hasAttribute('data-tip-title')) return
      // React đổi `title` trong lúc rê → giữ bản mới của React
      if (!el.hasAttribute('title')) el.setAttribute('title', el.getAttribute('data-tip-title')!)
      el.removeAttribute('data-tip-title')
    }

    const hide = () => {
      window.clearTimeout(timer)
      observer.disconnect()
      restore(current)
      current = null
      currentTarget = null
      shown = false
      setTip((t) => {
        if (t) lastShownAt = Date.now()
        return null
      })
    }

    const show = (target: Target) => {
      if (current !== target.el || !target.el.isConnected) return
      const text = target.text()
      if (!text) return
      shown = true
      setTip({ text, rect: target.el.getBoundingClientRect() })
    }

    const check = () => {
      frame = 0
      const target = targetAt(point.x, point.y)
      const el = target?.el ?? null
      if (el === current) return
      hide()
      if (el !== suppressed) suppressed = null
      if (!target || !el || el === suppressed) return
      // đang gõ trong ô / đang mở danh sách chọn → không che
      if (el.matches('input') && el === document.activeElement && !(el as HTMLInputElement).disabled) return
      if (el.getAttribute('aria-expanded') === 'true') return
      current = el
      currentTarget = target
      stash(el)
      observer.observe(el, { attributes: true, attributeFilter: ['title'] })
      const delay = Date.now() - lastShownAt < SKIP_DELAY_MS ? 0 : SHOW_DELAY_MS
      timer = window.setTimeout(() => show(target), delay)
    }

    const onMove = (e: PointerEvent | MouseEvent) => {
      if (e instanceof PointerEvent && e.pointerType === 'touch') return
      point = { x: e.clientX, y: e.clientY }
      if (!frame) frame = requestAnimationFrame(check)
    }
    /** Con trỏ ra khỏi cửa sổ / vào khung nhúng (`relatedTarget` null) */
    const onOut = (e: PointerEvent) => {
      if (!e.relatedTarget) hide()
    }
    const onDown = () => {
      suppressed = current
      hide()
    }

    document.addEventListener('pointermove', onMove, { passive: true })
    // nội dung cuộn / đổi dưới con trỏ đứng yên: không có pointermove nhưng có mouseover
    document.addEventListener('mouseover', onMove, { passive: true })
    document.addEventListener('pointerout', onOut, { passive: true })
    document.addEventListener('pointerdown', onDown, true)
    document.addEventListener('keydown', hide, true)
    document.addEventListener('scroll', hide, true)
    document.addEventListener('pointerleave', hide)
    window.addEventListener('blur', hide)
    return () => {
      cancelAnimationFrame(frame)
      hide()
      document.removeEventListener('pointermove', onMove)
      document.removeEventListener('mouseover', onMove)
      document.removeEventListener('pointerout', onOut)
      document.removeEventListener('pointerdown', onDown, true)
      document.removeEventListener('keydown', hide, true)
      document.removeEventListener('scroll', hide, true)
      document.removeEventListener('pointerleave', hide)
      window.removeEventListener('blur', hide)
    }
  }, [])

  // đặt dưới phần tử; không đủ chỗ thì lên trên; không tràn mép phải
  useLayoutEffect(() => {
    const box = boxRef.current
    if (!box) return
    if (!tip) {
      box.style.visibility = 'hidden'
      return
    }
    const { rect } = tip
    const w = box.offsetWidth
    const h = box.offsetHeight
    const below = rect.bottom + GAP
    const top = below + h > window.innerHeight - 8 && rect.top - GAP - h > 8 ? rect.top - GAP - h : below
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - w - 8))
    box.style.transform = `translate(${Math.round(left)}px, ${Math.round(top)}px)`
    box.style.visibility = 'visible'
  }, [tip])

  // Popup LUÔN nằm sẵn trong <body>, chỉ đổi chữ / chỗ / ẩn hiện. Không gắn / gỡ mỗi lần: thêm / bớt con của <body> bắt trình duyệt
  // tính lại bố cục cả trang (đo 03/11/2026, trang 60.000 phần tử: 100–150 ms mỗi lần hiện / ẩn → giật). `contain` khoanh việc
  // tính bố cục / kiểu trong hộp popup khi đổi chữ.
  // Cùng kiểu `TooltipContent` (`tooltipSurface`) để popup trong app trông như một.
  return createPortal(
    <div
      ref={boxRef}
      role="tooltip"
      aria-hidden={!tip}
      style={{ visibility: 'hidden', top: 0, left: 0, contain: 'layout style' }}
      className={cn(tooltipSurface, 'pointer-events-none fixed z-[100] max-w-[min(480px,calc(100vw-16px))] whitespace-pre-wrap break-words')}
    >
      {tip?.text}
    </div>,
    document.body,
  )
}
